import { useMutation } from '@apollo/client/react';
import { useContext, useMemo } from 'react';
import {
	type DeleteProjectItemsMutation,
	type DeleteProjectItemsMutationVariables,
	type DeleteProjectGroupsMutation,
	type DeleteProjectGroupsMutationVariables,
	type MergeProjectCartsToSessionCartMutation,
	type MergeProjectCartsToSessionCartMutationVariables,
	type ProjectGroupItemSource,
	type ProjectItemFieldsFragment
} from '../../__generated__/graphql-client-types';
import { ITEM_ADDED_TO_CART, ITEM_ADDED_TO_CART_FAIL, ITEM_ADDED_TO_CART_INELIGIBLE } from '../../constants/message';
import {
	type ProductSelectionInput,
	ProjectSelectionContext,
	type SelectionInput,
	SelectionInputType
} from '../../contexts/projects/project-selection/project-selection.context';
import { isShowroomOnly } from '../../helpers/project/project.helper';
import { CART_HEADER } from '../../queries/cart/cart-header.queries';
import { DELETE_PROJECT_ITEMS, DELETE_PROJECT_GROUPS, MERGE_PROJECT_GROUP_SOURCES } from '../../queries/project/project-details.queries';
import type {
	GroupSelection,
	ProductSelection,
	ProjectDetailsGroup,
	ProjectDetailsGroupItem,
	SelectionMap
} from '../../types/project.types';
import { useNotification } from '../notification/notification.hooks';
import { useProjectDetails, useProjectId } from './project-details.hooks';
import { useProjectGroups } from './project-group.hooks';

const mapItemToSelectionItem = (item: ProjectDetailsGroupItem, groupId: number): ProductSelection => ({
	groupId,
	itemId: item.id,
	purchased: Boolean(item.quantityPurchased),
	showroomOnly: isShowroomOnly(item.productPricingDisplay),
	isCustomProduct: item.type === 'SPECIAL_PRODUCT'
});

const itemMatchesInput = (item: ProjectDetailsGroupItem, selectionInput: SelectionInput) =>
	selectionInput.type === SelectionInputType.PRODUCT &&
	item.id !== selectionInput.itemId &&
	Boolean(item.quantityPurchased) === selectionInput.purchased;

const itemDoesntMatchInput = (item: ProjectDetailsGroupItem, selectionInput: SelectionInput) => !itemMatchesInput(item, selectionInput);

const selectionMatchesInput = (selection: ProductSelection, selectionInput: ProductSelectionInput) =>
	selection.itemId === selectionInput.itemId && selection.purchased === selectionInput.purchased;

const getSelectedGroups = (selection: SelectionMap, groups: ProjectDetailsGroup[]) => {
	return Object.entries(selection)
		.filter(([, selectionData]) => selectionData.allProductsSelected)
		.map(([groupId]) => groups.find((group) => group.id === parseInt(groupId, 10)))
		.filter((group): group is ProjectDetailsGroup => Boolean(group));
};

/**
 * useProjectSelection - helpers to maintain project selection state, as well
 * as act on those selections (delete, add to cart etc.)
 */
export const useProjectSelection = () => {
	const projectId = useProjectId();
	const groups = useProjectGroups();
	const { project } = useProjectDetails();
	const projectCartId = project?.cartId || 0;
	const { selection, setSelection } = useContext(ProjectSelectionContext);
	const [deleteProjectItems, { loading: isDeletingItems }] = useMutation<DeleteProjectItemsMutation, DeleteProjectItemsMutationVariables>(
		DELETE_PROJECT_ITEMS
	);
	const [deleteProjectGroups] = useMutation<DeleteProjectGroupsMutation, DeleteProjectGroupsMutationVariables>(DELETE_PROJECT_GROUPS);
	const [mergeProjectCartsToSessionCart] = useMutation<
		MergeProjectCartsToSessionCartMutation,
		MergeProjectCartsToSessionCartMutationVariables
	>(MERGE_PROJECT_GROUP_SOURCES, { refetchQueries: [{ query: CART_HEADER }] });

	const { notifySuccess, notifyDanger } = useNotification();

	const allSelected = useMemo(
		() => groups.length && groups.every((group) => selection[group.id]?.allProductsSelected),
		[groups, selection]
	);

	const selectedItemsAndGroupsCount = useMemo(() => {
		let selectedItemCount = 0;
		let selectedGroupCount = 0;

		Object.keys(selection).forEach((groupId) => {
			selectedItemCount += selection[groupId].selectedProducts?.length || 0;
			if (selection[groupId].allProductsSelected) {
				selectedGroupCount += 1;
			}
		});

		return {
			selectedItemCount,
			selectedGroupCount,
			totalSelected: selectedItemCount + selectedGroupCount
		};
	}, [selection]);

	const getGroupItemCount = (groupId: number): number => {
		const foundGroup = groups?.find((group) => group.id === groupId);
		return (foundGroup?.pending?.length || 0) + (foundGroup?.purchased?.length || 0);
	};

	const selectedGroupIds = useMemo(() => Object.keys(selection), [selection]);

	const selectedVariantIds = useMemo(() => {
		let variantIds: number[] = [];

		Object.keys(selection).forEach((groupId) => {
			const selectedProducts: ProductSelection[] = selection[groupId].selectedProducts;
			const selectedGroup = groups?.find((group) => group.id === parseInt(groupId, 10));
			if (!selectedGroup) {
				return;
			}
			const selectedItemIds = selectedProducts
				?.map(
					(product) =>
						selectedGroup[product.purchased ? 'purchased' : 'pending']?.find((item) => item.id === product.itemId)
							?.productVariantId
				)
				.filter((entry): entry is number => typeof entry === 'number' && entry !== 0);
			variantIds = variantIds.concat(selectedItemIds);
		});
		return variantIds;
	}, [selection, groups]);

	const noneSelected = useMemo(() => Object.keys(selection).length === 0, [selection]);

	const getNewSelectionState = (
		selectionInput: SelectionInput,
		selectedProducts: ProductSelection[]
	): {
		groupId: number;
		allProductsSelected: boolean;
		selectedProducts: ProductSelection[];
	} => {
		if (selectionInput.type === SelectionInputType.GROUP) {
			return {
				groupId: selectionInput.groupId,
				allProductsSelected: true,
				selectedProducts
			};
		} else {
			return {
				groupId: selectionInput.groupId,
				allProductsSelected: false,
				selectedProducts: [
					{
						groupId: selectionInput.groupId,
						itemId: selectionInput.itemId,
						purchased: selectionInput.purchased,
						showroomOnly: selectionInput.showroomOnly,
						isCustomProduct: selectionInput.isCustomProduct
					}
				]
			};
		}
	};

	const handleGroupSelection = (
		existingGroupSelection: GroupSelection,
		newSelectionState: SelectionMap,
		selectionInput: SelectionInput,
		newGroupSelection: GroupSelection,
		selectedProducts: ProductSelection[]
	): void => {
		if (existingGroupSelection.allProductsSelected) {
			delete newSelectionState[selectionInput.groupId];
		} else {
			newGroupSelection.allProductsSelected = true;
			newGroupSelection.selectedProducts = selectedProducts;
			newSelectionState[selectionInput.groupId] = newGroupSelection;
		}
	};

	const handleProductSelection = (
		existingGroupSelection: GroupSelection,
		newGroupSelection: GroupSelection,
		selectionInput: ProductSelectionInput,
		groupPending: ProjectItemFieldsFragment[],
		groupPurchased: ProjectItemFieldsFragment[]
	): void => {
		if (existingGroupSelection.selectedProducts.find((productSelection) => selectionMatchesInput(productSelection, selectionInput))) {
			newGroupSelection.allProductsSelected = false;
			newGroupSelection.selectedProducts = existingGroupSelection.selectedProducts.filter(
				(productSelection) => !selectionMatchesInput(productSelection, selectionInput)
			);
		} else {
			newGroupSelection.selectedProducts.push({
				groupId: selectionInput.groupId,
				itemId: selectionInput.itemId,
				purchased: selectionInput.purchased,
				showroomOnly: selectionInput.showroomOnly,
				isCustomProduct: selectionInput.isCustomProduct
			});
			newGroupSelection.allProductsSelected =
				newGroupSelection.selectedProducts.length === (groupPending.length || 0) + (groupPurchased.length || 0);
		}
	};

	return {
		allSelected,
		noneSelected,
		isDeletingItems,
		selectedGroupIds,
		selectedVariantIds,
		selectedItemsAndGroupsCount,
		getGroupItemCount,
		clearAll: () => {
			setSelection({});
		},
		selectAll: () => {
			const newSelection = { ...selection };
			groups.forEach((group) => {
				const itemList = [...(group.pending || []), ...(group.purchased || [])];
				newSelection[group.id] = {
					allProductsSelected: true,
					selectedProducts: itemList.map((item) => mapItemToSelectionItem(item, group.id))
				};
			});
			setSelection(newSelection);
		},
		itemSelected: (itemInQuestion: SelectionInput) => {
			const existingGroupSelection = selection[itemInQuestion.groupId];
			if (itemInQuestion.type === SelectionInputType.GROUP) {
				return Boolean(existingGroupSelection && existingGroupSelection.allProductsSelected);
			} else {
				const itemExistsInGroup = existingGroupSelection?.selectedProducts?.find((selectedItem) => {
					return selectedItem.itemId === itemInQuestion.itemId && selectedItem.purchased === itemInQuestion.purchased;
				});
				return Boolean(existingGroupSelection && (existingGroupSelection.allProductsSelected || itemExistsInGroup));
			}
		},
		toggleSelection: (selectionInput: SelectionInput) => {
			const existingGroupSelection = selection[selectionInput.groupId];
			const newSelectionState = { ...selection };
			const groupDataReference = groups.find((group) => group.id === selectionInput.groupId);

			if (groupDataReference) {
				const groupPending = groupDataReference.pending || [];
				const groupPurchased = groupDataReference.purchased || [];
				const pendingItems = groupPending.filter((item) => itemDoesntMatchInput(item, selectionInput));
				const purchasedItems = groupPurchased.filter((item) => itemDoesntMatchInput(item, selectionInput));

				const mappedGroupItemsToSelection = pendingItems
					.map((pendingItem) => mapItemToSelectionItem(pendingItem, selectionInput.groupId))
					.concat(purchasedItems.map((purchasedItem) => mapItemToSelectionItem(purchasedItem, selectionInput.groupId)));

				if (!existingGroupSelection) {
					newSelectionState[selectionInput.groupId] = getNewSelectionState(selectionInput, mappedGroupItemsToSelection);
				} else {
					const newGroupSelection = {
						...existingGroupSelection
					};
					if (selectionInput.type === SelectionInputType.GROUP) {
						handleGroupSelection(
							existingGroupSelection,
							newSelectionState,
							selectionInput,
							newGroupSelection,
							mappedGroupItemsToSelection
						);
					} else {
						handleProductSelection(existingGroupSelection, newGroupSelection, selectionInput, groupPending, groupPurchased);

						if (!newGroupSelection.selectedProducts.length) {
							delete newSelectionState[selectionInput.groupId];
						} else {
							newSelectionState[selectionInput.groupId] = newGroupSelection;
						}
					}
				}
				setSelection({ ...newSelectionState });
			}
		},
		deleteSelection: async () => {
			const itemPayload = Object.entries(selection)
				.filter(([, selectionData]) => !selectionData.allProductsSelected)
				.map(([groupId, groupSelection]) => {
					return groupSelection.selectedProducts.map((item) => {
						const cartId = groups.find((group) => group.id === parseInt(groupId, 10))?.cartId;
						return {
							isSingleCartProject: Boolean(projectCartId),
							cartId: Number(cartId),
							groupId: item.groupId,
							itemId: item.itemId
						};
					});
				})
				.flat();

			if (itemPayload.length) {
				await deleteProjectItems({ variables: { input: { projectId, items: itemPayload } } });
			}

			const groupIds = getSelectedGroups(selection, groups).map((group) => group.id);
			if (groupIds.length) {
				await deleteProjectGroups({
					variables: {
						input: { projectId, groupIds }
					}
				});
			}

			setSelection({});
		},
		addSelectionToCart: async () => {
			const areAllItemsIneligible: boolean = Object.entries(selection).every(([, selectionData]) =>
				selectionData.selectedProducts.every((product) => product.showroomOnly || product.isCustomProduct)
			);
			if (areAllItemsIneligible) {
				notifyDanger(ITEM_ADDED_TO_CART_INELIGIBLE);
				return;
			}

			const sources = Object.entries(selection).map(([groupId, selectionData]) => {
				const hasShowroomOnlyProduct = selectionData.selectedProducts.some((product) => product.showroomOnly);
				const shouldAddAll = selectionData.allProductsSelected && !hasShowroomOnlyProduct;
				return {
					shoppingListGroupId: parseInt(groupId, 10),
					allItems: shouldAddAll,
					sourceItems: shouldAddAll
						? undefined
						: selectionData.selectedProducts
								.filter((product) => !product.showroomOnly)
								.filter((product) => !product.isCustomProduct)
								.map(({ itemId, purchased }) => {
									const item: ProjectGroupItemSource = {
										itemId,
										status: purchased ? 'PURCHASED' : 'UNPURCHASED'
									};
									return item;
								})
				};
			});
			const filteredSources = sources.filter(
				(source) => source.allItems || (!source.allItems && source.sourceItems && source.sourceItems.length > 0)
			);
			await mergeProjectCartsToSessionCart({ variables: { input: { projectId, sources: filteredSources } } })
				.then(() => notifySuccess(ITEM_ADDED_TO_CART))
				.catch(() => notifyDanger(ITEM_ADDED_TO_CART_FAIL));
		}
	};
};
